// =====================================================================================
// FILE: src/components/modal/EditModal.jsx
// =====================================================================================

import React, { useState, useEffect } from "react";
import { THEME } from "../../constants/theme";
import { assetIdOf } from "../../utils/assetUtils";
import InlineAssetPicker from "../pickers/InlineAssetPicker";

export default function EditModal({ 
  item, 
  onClose, 
  onSave, 
  isLedMode, 
  isIptvMode,
  assetCache,
  iptvOptions,
}) {
  const [formData, setFormData] = useState({
    name: "",
    clientSponsorName: "",
    notes: "",
    clubInstructions: "",
    allocationId: "",
    rotations: 0,
    selectTiming: [],
    selectLocation: [],
    iptvGfxId: null,
    bigGfxVideosId: null,
    bigTvcId: null,
    mcgTiledLedId: null,
    marvelTiledLedId: null, // ✅ NEW
    superFullId: null,
    super169Id: null,
    superSideId: null,
    mcgInterchangeId: null,
    marvelInterchangeId: null,
    marvel360Id: null,
    mcg360Id: null,
  });

  const [editingAsset, setEditingAsset] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || "",
        clientSponsorName: item.clientSponsorName || "",
        notes: item.notes || "",
        clubInstructions: item.clubInstructions || "",
        allocationId: item.allocationId || "",
        rotations: item.rotations || 0,
        selectTiming: item.selectTiming || [],
        selectLocation: item.selectLocation || [],
        iptvGfxId: item.iptvGfxId || null,
        bigGfxVideosId: item.bigGfxVideosId || null,
        bigTvcId: item.bigTvcId || null,
        mcgTiledLedId: item.mcgTiledLedId || null,
        marvelTiledLedId: item.marvelTiledLedId || null, // ✅ NEW
        superFullId: item.superFullId || null,
        super169Id: item.super169Id || null,
        superSideId: item.superSideId || null,
        mcgInterchangeId: item.mcgInterchangeId || null,
        marvelInterchangeId: item.marvelInterchangeId || null,
        marvel360Id: item.marvel360Id || null,
        mcg360Id: item.mcg360Id || null,
      });
    }
  }, [item]);

  const getAsset = (id) => {
    if (!id) return null;
    return assetCache.get(id) || { id, title: id, destination: "", thumb: "" };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 20,
  };

  const modalStyle = {
    background: "white",
    borderRadius: 18,
    padding: 24,
    maxWidth: 700,
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  };

  const fieldGroupStyle = {
    marginBottom: 20,
  };

  const labelStyle = {
    display: "block",
    fontWeight: 900,
    fontSize: 13,
    color: THEME.text,
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    border: `1px solid ${THEME.line}`,
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 14,
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: 80,
    resize: "vertical",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    marginTop: 24,
  };

  const buttonStyle = (primary = false) => ({
    border: primary ? `1px solid ${THEME.primary}` : `1px solid ${THEME.line}`,
    background: primary ? THEME.primary : "white",
    color: primary ? "white" : THEME.text,
    borderRadius: 12,
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
  });

  const toggleMultiSelect = (field, value) => {
    const current = formData[field] || [];
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    setFormData({ ...formData, [field]: Array.from(next) });
  };

  const renderAssetField = (label, field, assetId) => {
    const asset = getAsset(assetId);
    const isEditing = editingAsset === field;

    return (
      <div style={fieldGroupStyle}>
        <label style={labelStyle}>{label}</label>
        {!isEditing ? (
          <div
            onClick={() => setEditingAsset(field)}
            style={{
              ...inputStyle,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              minHeight: 44,
            }}
          >
            {asset ? (
              <>
                {asset.thumb && (
                  <img
                    src={asset.thumb}
                    alt=""
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      objectFit: "cover",
                    }}
                  />
                )}
                <span style={{ fontWeight: 900 }}>{asset.title}</span>
              </>
            ) : (
              <span style={{ opacity: 0.5 }}>Click to select asset...</span>
            )}
          </div>
        ) : (
          <InlineAssetPicker
            current={asset}
            disabled={false}
            onPick={(pickedAsset) => {
              setFormData({ ...formData, [field]: assetIdOf(pickedAsset) });
              setEditingAsset(null);
            }}
            onClear={() => {
              setFormData({ ...formData, [field]: null });
              setEditingAsset(null);
            }}
            onClose={() => setEditingAsset(null)}
          />
        )}
      </div>
    );
  };

  const renderMultiSelect = (label, field, options) => {
    const selected = formData[field] || [];
    return (
      <div style={fieldGroupStyle}>
        <label style={labelStyle}>{label}</label>
        <div
          style={{
            border: `1px solid ${THEME.line}`,
            borderRadius: 12,
            padding: 12,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {options.map((option) => (
            <label
              key={option}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 6px",
                cursor: "pointer",
                borderRadius: 8,
                background: selected.includes(option) ? THEME.chip : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleMultiSelect(field, option)}
              />
              <span style={{ fontSize: 13, fontWeight: 900 }}>{option}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <h2 style={{ margin: "0 0 20px 0", fontSize: 20, fontWeight: 1000 }}>
          Edit Content
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Common Fields */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Client/Sponsor</label>
            <input
              type="text"
              value={formData.clientSponsorName}
              onChange={(e) => setFormData({ ...formData, clientSponsorName: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              style={textareaStyle}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Club Instructions</label>
            <textarea
              value={formData.clubInstructions}
              onChange={(e) => setFormData({ ...formData, clubInstructions: e.target.value })}
              style={textareaStyle}
            />
          </div>

          {/* LED Mode Fields */}
          {isLedMode && (
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Allocation ID</label>
              <input
                type="text"
                value={formData.allocationId}
                onChange={(e) => setFormData({ ...formData, allocationId: e.target.value })}
                style={inputStyle}
              />
            </div>
          )}

          {/* IPTV Mode Fields */}
          {isIptvMode && (
            <>
              {renderAssetField("IPTV Graphic", "iptvGfxId", formData.iptvGfxId)}

              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Rotations</label>
                <input
                  type="number"
                  min="0"
                  value={formData.rotations}
                  onChange={(e) => setFormData({ ...formData, rotations: parseInt(e.target.value) || 0 })}
                  style={inputStyle}
                />
              </div>

              {iptvOptions && renderMultiSelect("Select Timing", "selectTiming", iptvOptions.timing || [])}
              {iptvOptions && renderMultiSelect("Select Location", "selectLocation", iptvOptions.location || [])}
            </>
          )}

          {/* Non-IPTV Asset Fields */}
          {!isIptvMode && (
            <>
              {renderAssetField("Big GFX Videos", "bigGfxVideosId", formData.bigGfxVideosId)}
              {renderAssetField("Big TVC", "bigTvcId", formData.bigTvcId)}
              {renderAssetField("MCG Tiled LED", "mcgTiledLedId", formData.mcgTiledLedId)}
              {renderAssetField("Marvel Tiled LED", "marvelTiledLedId", formData.marvelTiledLedId)}
              {renderAssetField("Super Full", "superFullId", formData.superFullId)}
              {renderAssetField("Super 20:9", "super169Id", formData.super169Id)}
              {renderAssetField("Super Side", "superSideId", formData.superSideId)}
              {renderAssetField("MCG Interchange", "mcgInterchangeId", formData.mcgInterchangeId)}
              {renderAssetField("Marvel Interchange", "marvelInterchangeId", formData.marvelInterchangeId)}
              {renderAssetField("Marvel 360", "marvel360Id", formData.marvel360Id)}
              {renderAssetField("MCG 360", "mcg360Id", formData.mcg360Id)}
            </>
          )}

          <div style={buttonContainerStyle}>
            <button type="button" onClick={onClose} style={buttonStyle(false)}>
              Cancel
            </button>
            <button type="submit" style={buttonStyle(true)}>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
